import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { partialAPI } from '@/api/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, MapPin, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateWithDay, formatTime12Hour, formatTimeRange } from '@/utils/formatters';

const PartialExamTab = () => {
  const { refreshCredits, credits, moduleCredits } = useAuth();
  const [selectedModule, setSelectedModule] = useState('LISTENING');
  const [slots, setSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Speaking dates states
  const [speakingDates, setSpeakingDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateSlots, setDateSlots] = useState([]);
  const [showSlotsModal, setShowSlotsModal] = useState(false);
  const [loadingDateSlots, setLoadingDateSlots] = useState(false);

  // Booking confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedSlotForBooking, setSelectedSlotForBooking] = useState(null);
  const [bookingInProgress, setBookingInProgress] = useState(false);

  const modules = [
    { value: 'LISTENING', label: 'Listening', color: 'from-blue-500 to-cyan-500' },
    { value: 'READING', label: 'Reading', color: 'from-green-500 to-emerald-500' },
    { value: 'WRITING', label: 'Writing', color: 'from-purple-500 to-pink-500' },
    { value: 'SPEAKING', label: 'Speaking', color: 'from-orange-500 to-red-500' }
  ];

  useEffect(() => {
    if (selectedModule === 'SPEAKING') {
      fetchSpeakingDates();
    } else {
      fetchSlots();
    }
    fetchMyBookings();
    
    // Listen for cancellation events
    const handleBookingCancelled = () => {
      if (selectedModule === 'SPEAKING') {
        fetchSpeakingDates();
      } else {
        fetchSlots();
      }
      fetchMyBookings();
    };
    
    window.addEventListener('bookingCancelled', handleBookingCancelled);
    
    return () => {
      window.removeEventListener('bookingCancelled', handleBookingCancelled);
    };
  }, [selectedModule]);

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const response = await partialAPI.getSlots({ module: selectedModule });
      setSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      toast.error('Failed to load exam slots');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpeakingDates = async () => {
    setLoading(true);
    try {
      const response = await partialAPI.getSpeakingDates();
      setSpeakingDates(response.data);
    } catch (error) {
      console.error('Failed to fetch speaking dates:', error);
      toast.error('Failed to load speaking dates');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlotsForDate = async (date) => {
    setLoadingDateSlots(true);
    setSelectedDate(date);
    setShowSlotsModal(true);
    
    try {
      const response = await partialAPI.getSpeakingSlotsByDate(date);
      setDateSlots(response.data);
    } catch (error) {
      console.error('Failed to fetch slots for date:', error);
      toast.error('Failed to load slots');
      setDateSlots([]);
    } finally {
      setLoadingDateSlots(false);
    }
  };

  const fetchMyBookings = async () => {
    try {
      const response = await partialAPI.getMyBookings();
      setMyBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleBook = async (slotId) => {
    setBookingInProgress(true);
    try {
      const response = await partialAPI.book(slotId);
      toast.success('Exam booked successfully!');
      
      // Close confirmation modal
      setShowConfirmModal(false);
      setSelectedSlotForBooking(null);
      
      // Refresh data
      if (selectedModule === 'SPEAKING') {
        await fetchSpeakingDates();
      } else {
        await fetchSlots();
      }
      await fetchMyBookings();
      await refreshCredits();
    } catch (error) {
      console.error('Booking error:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMsg = 'Failed to book exam';
      
      // Handle different error response formats
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (validation errors), extract first message
        if (Array.isArray(detail)) {
          const firstError = detail[0];
          errorMsg = firstError?.msg || JSON.stringify(firstError) || errorMsg;
        } else if (typeof detail === 'string') {
          // Simple string error message
          errorMsg = detail;
        }
      }
      
      // Always show error message
      toast.error(errorMsg);
    } finally {
      setBookingInProgress(false);
    }
  };

  // Open confirmation modal with slot details
  const openBookingConfirmation = (slot) => {
    setSelectedSlotForBooking(slot);
    setShowConfirmModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Credits Display */}
      <Card className="backdrop-blur-lg bg-gradient-to-br from-purple-50 to-blue-50 shadow-lg border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-full">
                <Coins className="h-7 w-7 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Partial Exam Credits</CardTitle>
                <CardDescription className="text-sm text-gray-600 mt-0.5">
                  Available credits for booking
                </CardDescription>
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-purple-600">
                {(() => {
                  const total = (moduleCredits?.listening || 0) + (moduleCredits?.reading || 0) + (moduleCredits?.writing || 0) + (moduleCredits?.speaking || 0);
                  return total;
                })()}
              </div>
              <p className="text-sm text-gray-600 font-medium mt-1">Remaining</p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Module Selection */}
      <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
        <CardHeader>
          <CardTitle>Select Module</CardTitle>
          <CardDescription>Choose which exam module you want to book</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {modules.map((module) => {
              const creditCount = moduleCredits?.[module.value.toLowerCase()] || 0;
              return (
                <button
                  key={module.value}
                  onClick={() => setSelectedModule(module.value)}
                  className={`p-6 rounded-xl transition-all transform hover:scale-105 ${
                    selectedModule === module.value
                      ? `bg-gradient-to-br ${module.color} text-white shadow-lg`
                      : 'bg-gradient-to-br from-gray-50 to-gray-100 text-gray-700 hover:shadow-md'
                  }`}
                >
                  <div className="font-semibold text-lg">{module.label}</div>
                  <div className={`text-sm mt-1 ${selectedModule === module.value ? 'text-white/90' : 'text-gray-600'}`}>
                    {String(creditCount)} remaining
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Available Slots - Speaking Dates or Regular Slots */}
      <Card className="backdrop-blur-lg bg-white/80 shadow-lg border-0">
        <CardHeader>
          <CardTitle>Available Slots - {selectedModule}</CardTitle>
          <CardDescription>
            {selectedModule === 'SPEAKING' 
              ? 'Select a date to view available speaking slots' 
              : `Book your ${selectedModule.toLowerCase()} exam`
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : selectedModule === 'SPEAKING' ? (
            // Speaking Dates View
            speakingDates.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg font-semibold">No speaking slots available</p>
                <p className="text-gray-500 mt-2">Please check back later or contact admin</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {speakingDates.map((dateInfo) => {
                  const isFull = dateInfo.available_slots === 0;
                  const hasMyBooking = dateInfo.booked; // User has already booked on this date
                  
                  return (
                    <div
                      key={dateInfo.date}
                      onClick={() => !isFull && fetchSlotsForDate(dateInfo.date)}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        hasMyBooking
                          ? 'bg-green-50 border-green-300'
                          : isFull
                          ? 'bg-gradient-to-r from-red-100 to-orange-100 border-red-400 opacity-80 cursor-not-allowed'
                          : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 hover:shadow-lg hover:scale-105'
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge className={hasMyBooking ? 'bg-green-600' : isFull ? 'bg-gradient-to-r from-red-600 to-orange-500 text-white' : 'bg-green-600'}>
                            {hasMyBooking ? 'Your Booking' : isFull ? 'Booked' : 'Available'}
                          </Badge>
                          <span className="text-sm font-semibold text-gray-700">
                            {dateInfo.available_slots}/{dateInfo.total_slots} slots
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Calendar className={`w-5 h-5 ${hasMyBooking ? 'text-green-600' : isFull ? 'text-red-600' : 'text-green-600'}`} />
                            <span className="font-semibold text-lg">{formatDateWithDay(dateInfo.date)}</span>
                          </div>
                          <p className="text-sm text-gray-600">
                            {isFull ? 'All slots booked' : 'Click to view available time slots'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            // Regular Slots View (Listening, Reading, Writing)
            slots.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg font-semibold">No slots available</p>
                <p className="text-gray-500 mt-2">Please check back later or contact admin</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {slots.map((slot) => {
                  const isBooked = myBookings.some(b => b.partial_id === slot.id && b.status === 'BOOKED');
                  const isFull = slot.remaining_seats === 0;
                  
                  return (
                    <div
                      key={slot.id}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isBooked
                          ? 'bg-green-50 border-green-300'
                          : isFull
                          ? 'bg-gray-50 border-gray-300 opacity-60'
                          : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-300 hover:shadow-lg'
                      }`}
                      data-testid={`slot-${slot.id}`}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Badge className={isBooked ? 'bg-green-600' : isFull ? 'bg-gray-500' : 'bg-blue-600'}>
                            {isBooked ? 'Your Booking' : isFull ? 'Full' : 'Available'}
                          </Badge>
                          <span className="text-sm font-semibold text-gray-700">
                            {slot.remaining_seats}/{slot.total_slots} seats
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold">{formatDateWithDay(slot.exam_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span>{formatTimeRange(slot.start_time, slot.end_time)}</span>
                          </div>
                          {slot.room && (
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span>{slot.room}{slot.campus && `, ${slot.campus}`}</span>
                            </div>
                          )}
                        </div>

                        {!isBooked && !isFull && (
                          <Button
                            onClick={() => {
                              setShowSlotsModal(false);
                              openBookingConfirmation(slot);
                            }}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 mt-3"
                            size="sm"
                          >
                            Book Now
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Speaking Slots Modal */}
      <Dialog open={showSlotsModal} onOpenChange={setShowSlotsModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Speaking Slots - {selectedDate && formatDateWithDay(selectedDate)}</DialogTitle>
            <DialogDescription>Select a time slot to book your speaking exam</DialogDescription>
          </DialogHeader>
          
          {loadingDateSlots ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <p className="mt-4 text-gray-600">Loading slots...</p>
            </div>
          ) : dateSlots.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="mx-auto h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-600">No slots available for this date</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dateSlots.map((slot) => {
                const isBooked = myBookings.some(b => b.partial_id === slot.id && b.status === 'BOOKED');
                const isFull = slot.remaining_seats === 0;
                
                return (
                  <div
                    key={slot.id}
                    className={`p-4 rounded-lg border-2 ${
                      isBooked
                        ? 'bg-green-50 border-green-300'
                        : isFull
                        ? 'bg-gradient-to-br from-red-50 to-orange-50 border-red-300'
                        : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-300'
                    }`}
                  >
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge className={isBooked ? 'bg-green-600' : isFull ? 'bg-red-600' : 'bg-blue-600'}>
                          {isBooked ? 'Your Booking' : isFull ? 'This Slot is Taken' : 'Available'}
                        </Badge>
                        <span className="text-sm font-semibold text-gray-700">
                          {slot.remaining_seats}/{slot.total_slots} seats
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="font-semibold">{formatTimeRange(slot.start_time, slot.end_time)}</span>
                        </div>
                        {slot.room && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span>{slot.room}{slot.campus && `, ${slot.campus}`}</span>
                          </div>
                        )}
                      </div>

                      {!isBooked && !isFull && (
                        <Button
                          onClick={() => {
                            setShowSlotsModal(false);
                            openBookingConfirmation({...slot, exam_date: selectedDate});
                          }}
                          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 mt-3"
                          size="sm"
                        >
                          Book Now
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Booking Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">বুকিং নিশ্চিত করুন</DialogTitle>
            <DialogDescription className="text-center">
              অনুগ্রহ করে নিচের তথ্য যাচাই করে Confirm করুন
            </DialogDescription>
          </DialogHeader>
          
          {selectedSlotForBooking && (
            <div className="space-y-4 py-4">
              {/* Module Badge */}
              <div className="flex justify-center">
                <Badge className={`text-lg px-4 py-2 bg-gradient-to-r ${modules.find(m => m.value === selectedModule)?.color || 'from-blue-500 to-purple-500'}`}>
                  {selectedModule}
                </Badge>
              </div>

              {/* Exam Details Card */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 space-y-3 border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">তারিখ</p>
                    <p className="font-bold text-gray-800">
                      {formatDateWithDay(selectedSlotForBooking.exam_date || selectedSlotForBooking.speak_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <Clock className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">সময়</p>
                    <p className="font-bold text-gray-800">
                      {formatTimeRange(selectedSlotForBooking.start_time, selectedSlotForBooking.end_time)}
                    </p>
                  </div>
                </div>

                {(selectedSlotForBooking.room || selectedSlotForBooking.campus) && (
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <MapPin className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">স্থান</p>
                      <p className="font-bold text-gray-800">
                        {selectedSlotForBooking.room}{selectedSlotForBooking.campus && `, ${selectedSlotForBooking.campus}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Credit Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <p className="text-sm text-yellow-800">
                  <Coins className="w-4 h-4 inline mr-1" />
                  এই বুকিংয়ের জন্য <strong>1 Credit</strong> কাটা হবে
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setSelectedSlotForBooking(null);
                  }}
                  disabled={bookingInProgress}
                >
                  বাতিল
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  onClick={() => handleBook(selectedSlotForBooking.id)}
                  disabled={bookingInProgress}
                >
                  {bookingInProgress ? 'বুকিং হচ্ছে...' : '✓ Confirm বুকিং'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartialExamTab;